import { useState, type FormEvent } from "react";
import { Field, PasswordInput } from "@/components/base/FormControls";
import { useSession } from "@/session/SessionProvider";
import { useChangePassword } from "@/hooks/useChangePassword";

export default function SecuriteSection() {
  const { session } = useSession();
  const { changePassword, saving } = useChangePassword();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState("");
  const [saved, setSaved] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    setSaved(false);

    const nextErrors: Record<string, string> = {};
    if (!current) nextErrors.current = "Saisissez votre mot de passe actuel.";
    if (!next) nextErrors.next = "Saisissez un nouveau mot de passe.";
    else if (next.length < 8) nextErrors.next = "Le mot de passe doit contenir au moins 8 caractères.";
    if (!confirm) nextErrors.confirm = "Confirmez le nouveau mot de passe.";
    else if (next && next !== confirm) nextErrors.confirm = "Les deux mots de passe ne correspondent pas.";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const result = await changePassword(session?.email ?? "", current, next);
    if (!result.ok) {
      setFormError(result.error);
      return;
    }
    reset();
    setSaved(true);
  };

  return (
    <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
          <i className="ri-lock-2-line text-base" aria-hidden="true" />
        </span>
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Sécurité</h3>
          <p className="text-xs text-foreground-500">Changez le mot de passe de votre compte d'agence.</p>
        </div>
      </header>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5 p-4 md:p-5">
        {formError && (
          <p className="flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50/50 px-3.5 py-2.5 text-xs text-primary-800">
            <i className="ri-error-warning-line text-sm" aria-hidden="true" />
            {formError}
          </p>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Mot de passe actuel" htmlFor="agence-secu-current" required error={errors.current} className="sm:col-span-2">
            <PasswordInput
              id="agence-secu-current"
              value={current}
              onChange={(event) => {
                setCurrent(event.target.value);
                setSaved(false);
              }}
              placeholder="••••••••"
              invalid={Boolean(errors.current)}
              autoComplete="current-password"
            />
          </Field>
          <Field label="Nouveau mot de passe" htmlFor="agence-secu-next" required error={errors.next}>
            <PasswordInput
              id="agence-secu-next"
              value={next}
              onChange={(event) => {
                setNext(event.target.value);
                setSaved(false);
              }}
              placeholder="Au moins 8 caractères"
              invalid={Boolean(errors.next)}
              autoComplete="new-password"
            />
          </Field>
          <Field label="Confirmer le nouveau mot de passe" htmlFor="agence-secu-confirm" required error={errors.confirm}>
            <PasswordInput
              id="agence-secu-confirm"
              value={confirm}
              onChange={(event) => {
                setConfirm(event.target.value);
                setSaved(false);
              }}
              placeholder="Retapez le mot de passe"
              invalid={Boolean(errors.confirm)}
              autoComplete="new-password"
            />
          </Field>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
          {saved ? (
            <p className="flex items-center gap-1.5 text-xs text-secondary-700">
              <i className="ri-checkbox-circle-line text-sm" aria-hidden="true" />
              Mot de passe mis à jour.
            </p>
          ) : (
            <span className="text-xs text-foreground-400">
              Votre mot de passe est géré par l'authentification sécurisée de votre compte.
            </span>
          )}
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className="ri-shield-keyhole-line text-base" aria-hidden="true" />
            {saving ? "Mise à jour…" : "Mettre à jour"}
          </button>
        </div>
      </form>
    </section>
  );
}